import React, { useState } from "react";
import { Box, Button, Collapse, MenuItem, Select, Stack, Typography } from "@mui/material";
import ActionRow from "./ActionRow";

export default function TodayActions({
  incomplete,
  completed,
  onActionClick,
  onSmallVersion,
  onResetToday,
  savingActionKey = "",
  empty,
  emptyTitle = "You cleared the habits in play",
  emptyKind = "cleared",
  hasActiveSprint = false,
  onSendUpdate,
  onOpenPlan,
  feedback = "",
  goalOptions = [],
  selectedGoalId = null,
  onSelectGoal,
}) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [showOtherHabits, setShowOtherHabits] = useState(false);
  const focusAction =
    incomplete.find((action) => action.recommended && !action.disabled) ||
    incomplete.find((action) => !action.disabled) ||
    incomplete[0] ||
    null;
  const secondaryActions = focusAction
    ? incomplete.filter((action) => String(action.id) !== String(focusAction.id))
    : incomplete;
  const secondaryOpenLabel = secondaryActions.length === 1
    ? "1 more open"
    : `${secondaryActions.length} more open`;

  return (
    <Box
      sx={{
        borderRadius: "22px",
        border: "1px solid #dbe7f3",
        backgroundColor: "#ffffff",
        p: { xs: 1.45, md: 1.65 },
        boxShadow: "0 12px 26px rgba(15, 35, 59, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          mb: 1.15,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 0, color: "#172540", fontSize: "1.12rem", lineHeight: 1.2 }}>
          Today’s habits
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 850,
            color: "#0f4f7a",
            fontSize: "0.8rem",
            borderRadius: 999,
            backgroundColor: "#e8f5f8",
            px: 0.95,
            py: 0.35,
          }}
        >
          {incomplete.length} open
        </Typography>
      </Box>

      {goalOptions.length > 1 ? (
        <Box sx={{ mb: 1 }}>
          <Select
            size="small"
            fullWidth
            value={selectedGoalId || goalOptions[0]?.id || ""}
            onChange={(event) => onSelectGoal?.(event.target.value)}
            sx={{
              borderRadius: "14px",
              bgcolor: "#f8fbff",
              fontWeight: 800,
              "& .MuiSelect-select": { py: 0.85, px: 1.15 },
            }}
          >
            {goalOptions.map((goal) => (
              <MenuItem key={goal.id} value={goal.id}>
                {goal.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : null}

      {feedback ? (
        <Typography
          variant="body2"
          sx={{
            mb: 1.1,
            px: 1.1,
            py: 0.82,
            borderRadius: "14px",
            backgroundColor: "#f0fdf4",
            color: "#166534",
            fontWeight: 700,
          }}
        >
          {feedback}
        </Typography>
      ) : null}

      {empty ? (
        <Box
          sx={{
            borderRadius: "16px",
            border: "1px dashed",
            borderColor: "#d6e1ec",
            backgroundColor: "#f8fafc",
            px: 1.2,
            py: 1.25,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {emptyTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.45, color: "#64748b" }}>
            {emptyKind === "none_due"
              ? "Use Plan if you want to shape the next step."
              : hasActiveSprint
                ? "Check in with your TrustBud, or shape what comes next."
                : "Shape what comes next in Plan."}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 0.75 }}>
            {emptyKind === "none_due" || !hasActiveSprint ? null : (
              <Button variant="contained" onClick={onSendUpdate} sx={{ textTransform: "none", borderRadius: "8px" }}>
                Check in with your TrustBud
              </Button>
            )}
            <Button variant="outlined" onClick={onOpenPlan} sx={{ textTransform: "none", borderRadius: "8px" }}>
              Open Plan
            </Button>
          </Stack>
        </Box>
      ) : focusAction ? (
        <Stack spacing={0.75}>
          <Box
            sx={{
            borderRadius: "18px",
            border: "1px solid #cfe0ee",
            backgroundColor: "#f6fbff",
            p: { xs: 0.95, sm: 1.05 },
          }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#17345d", textTransform: "uppercase", letterSpacing: 0.65 }}>
                  Start here
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.15, color: "#60738c", fontWeight: 700, fontSize: "0.86rem" }}>
                  Do one thing first. The rest can wait below.
                </Typography>
              </Box>
            </Box>
            <ActionRow
              action={focusAction}
              saving={savingActionKey === `${focusAction.id}:full` || savingActionKey === `${focusAction.id}:small_version` || savingActionKey === `${focusAction.id}:reset`}
              onClick={() => onActionClick(focusAction.id, "full")}
              onDone={() => onActionClick(focusAction.id, "full")}
              onSmallVersion={focusAction.backupTitle ? () => onSmallVersion?.(focusAction.id) : undefined}
              onReset={() => onResetToday?.(focusAction.id)}
            />
          </Box>

          {secondaryActions.length ? (
            <>
              <Button
                variant="text"
                onClick={() => setShowOtherHabits((prev) => !prev)}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  px: 0,
                  py: 0.65,
                  fontWeight: 850,
                  color: "#334155",
                  borderRadius: "14px",
                  backgroundColor: "#f6f8fb",
                }}
              >
                {showOtherHabits ? "Hide other habits" : `Show ${secondaryOpenLabel}`}
              </Button>
              <Collapse in={showOtherHabits}>
                <Stack spacing={0.65} sx={{ mt: 0.75 }}>
                  {secondaryActions.map((action) => (
                    <ActionRow
                      key={action.id}
                      action={action}
                      saving={savingActionKey === `${action.id}:full` || savingActionKey === `${action.id}:small_version` || savingActionKey === `${action.id}:reset`}
                      onClick={() => onActionClick(action.id, "full")}
                      onDone={() => onActionClick(action.id, "full")}
                      onSmallVersion={action.backupTitle ? () => onSmallVersion?.(action.id) : undefined}
                      onReset={() => onResetToday?.(action.id)}
                    />
                  ))}
                </Stack>
              </Collapse>
              {!showOtherHabits ? (
                <Typography variant="body2" sx={{ color: "#718198", fontWeight: 650, px: 0.3, fontSize: "0.84rem" }}>
                  Other habits are still in Today. Open them after the first action is handled.
                </Typography>
              ) : null}
            </>
          ) : null}
        </Stack>
      ) : (
        <Box
          sx={{
            borderRadius: "16px",
            border: "1px dashed #d6e1ec",
            backgroundColor: "#f8fafc",
            px: 1.2,
            py: 1.25,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 750 }}>
            All open habits are handled.
          </Typography>
        </Box>
      )}

      {completed.length ? (
        <Box sx={{ mt: 1.45 }}>
          <Button
            variant="text"
            onClick={() => setShowCompleted((prev) => !prev)}
            sx={{
              width: "100%",
              textTransform: "none",
              px: 0,
              py: 0.65,
              fontWeight: 800,
              color: "#334155",
              borderRadius: "14px",
              backgroundColor: "#f6f8fb",
            }}
          >
            {showCompleted ? "Hide completed" : `Show completed (${completed.length})`}
          </Button>
          <Collapse in={showCompleted}>
            <Stack spacing={0.65} sx={{ mt: 0.75 }}>
              {completed.map((action) => (
                <ActionRow key={action.id} action={action} onClick={() => onActionClick(action.id, "full")} />
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}
